import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import { buildApiUrl } from '../utils/serverOrigin';

const PUSH_ENABLED_KEY = 'clawlet.push.enabled';

export type PushPermissionState = NotificationPermission | 'unsupported';
export type PushSubscriptionState = 'idle' | 'syncing' | 'subscribed' | 'unsubscribed' | 'error';

export interface PushNotificationsController {
  supported: boolean;
  permissionState: PushPermissionState;
  subscriptionState: PushSubscriptionState;
  appPushEnabled: boolean;
  isBusy: boolean;
  lastError: string | null;
  promptVisible: boolean;
  statusLabel: string;
  statusHint: string;
  enablePush: () => Promise<void>;
  disablePush: () => Promise<void>;
  dismissPrompt: () => void;
  refresh: () => Promise<void>;
}

interface UsePushNotificationsOptions {
  connected: boolean;
  gatewayToken: string;
}

interface ServiceWorkerPushMessage {
  type?: string;
  ok?: boolean;
  error?: string;
  endpoint?: string;
}

interface PushConfigPayload {
  type: 'push-config';
  payload: {
    gatewayToken: string;
    userLabel: string;
    appEnabled: boolean;
  };
}

function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function loadAppPushEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const raw = window.localStorage.getItem(PUSH_ENABLED_KEY);
    return raw == null ? true : raw !== 'false';
  } catch {
    return true;
  }
}

function persistAppPushEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(PUSH_ENABLED_KEY, enabled ? 'true' : 'false');
  } catch {
    // Ignore persistence failures.
  }
}

function getUserLabel(): string {
  const displayName = useSettingsStore.getState().settings.general.displayName.trim();
  if (displayName) {
    return displayName;
  }

  if (typeof window !== 'undefined') {
    try {
      const stored =
        window.localStorage.getItem('clawlet.displayName') ??
        window.localStorage.getItem('displayName') ??
        window.localStorage.getItem('clawlet.userName');
      if (stored?.trim()) {
        return stored.trim();
      }
    } catch {
      // localStorage unavailable
    }
  }

  return 'User';
}

function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const normalized = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);

  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }

  return output;
}

function headersWithAuth(gatewayToken: string, withJson = false): HeadersInit {
  const headers: Record<string, string> = {};

  if (withJson) {
    headers['Content-Type'] = 'application/json';
  }

  if (gatewayToken.trim()) {
    headers.Authorization = `Bearer ${gatewayToken.trim()}`;
  }

  return headers;
}

async function fetchVapidPublicKey(gatewayToken: string): Promise<string> {
  const response = await fetch(buildApiUrl('/api/push/vapid-public-key', useAppStore.getState().gatewayUrl), {
    headers: headersWithAuth(gatewayToken),
  });

  const data = (await response.json().catch(() => ({}))) as { publicKey?: unknown; error?: unknown };

  if (!response.ok) {
    const message = typeof data.error === 'string' ? data.error : 'Unable to fetch VAPID public key.';
    throw new Error(message);
  }

  if (typeof data.publicKey !== 'string' || !data.publicKey.trim()) {
    throw new Error('Push server returned an invalid VAPID public key.');
  }

  return data.publicKey;
}

async function postSubscription(
  subscription: PushSubscription,
  gatewayToken: string,
  userLabel: string,
): Promise<void> {
  const json = subscription.toJSON();
  const response = await fetch(buildApiUrl('/api/push/subscribe', useAppStore.getState().gatewayUrl), {
    method: 'POST',
    headers: headersWithAuth(gatewayToken, true),
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      },
      userLabel,
    }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: unknown };
    const message = typeof data.error === 'string' ? data.error : 'Unable to save push subscription.';
    throw new Error(message);
  }
}

async function postConfigToServiceWorker(
  gatewayToken: string,
  userLabel: string,
  appEnabled: boolean,
): Promise<void> {
  if (!isPushSupported()) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const message: PushConfigPayload = {
    type: 'push-config',
    payload: {
      gatewayToken,
      userLabel,
      appEnabled,
    },
  };

  registration.active?.postMessage(message);
  navigator.serviceWorker.controller?.postMessage(message);
}

export function usePushNotifications({ connected, gatewayToken }: UsePushNotificationsOptions): PushNotificationsController {
  const [permissionState, setPermissionState] = useState<PushPermissionState>(
    isPushSupported() ? Notification.permission : 'unsupported',
  );
  const [subscriptionState, setSubscriptionState] = useState<PushSubscriptionState>('idle');
  const [appPushEnabled, setAppPushEnabled] = useState<boolean>(() => loadAppPushEnabled());
  const [isBusy, setIsBusy] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [promptDismissed, setPromptDismissed] = useState(false);

  const supported = useMemo(() => isPushSupported(), []);
  const userLabel = useSettingsStore((state) => state.settings.general.displayName);
  const effectiveUserLabel = userLabel.trim() || getUserLabel();

  const syncSubscription = useCallback(
    async (forceResubscribe = false) => {
      if (!supported || !connected) {
        return;
      }

      if (Notification.permission !== 'granted') {
        setPermissionState(Notification.permission);
        setSubscriptionState('unsubscribed');
        return;
      }

      if (!appPushEnabled) {
        setSubscriptionState('unsubscribed');
        return;
      }

      setSubscriptionState('syncing');
      setLastError(null);

      try {
        await postConfigToServiceWorker(gatewayToken, effectiveUserLabel, appPushEnabled);

        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription || forceResubscribe) {
          if (subscription && forceResubscribe) {
            await subscription.unsubscribe().catch(() => {
              // Ignore stale subscription cleanup failures.
            });
          }

          const publicKey = await fetchVapidPublicKey(gatewayToken);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: base64UrlToUint8Array(publicKey),
          });
        }

        await postSubscription(subscription, gatewayToken, effectiveUserLabel);
        setSubscriptionState('subscribed');
      } catch (error) {
        console.error('[usePushNotifications] Failed to sync push subscription:', error);
        setSubscriptionState('error');
        setLastError(error instanceof Error ? error.message : 'Unable to enable push notifications.');
      }
    },
    [appPushEnabled, connected, effectiveUserLabel, gatewayToken, supported],
  );

  const refresh = useCallback(async () => {
    if (!supported) {
      setPermissionState('unsupported');
      setSubscriptionState('unsubscribed');
      return;
    }

    const permission = Notification.permission;
    setPermissionState(permission);

    if (!connected) {
      setSubscriptionState('idle');
      return;
    }

    if (permission === 'granted' && appPushEnabled) {
      await syncSubscription();
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setSubscriptionState(subscription ? 'subscribed' : 'unsubscribed');
    } catch {
      setSubscriptionState('unsubscribed');
    }
  }, [appPushEnabled, connected, supported, syncSubscription]);

  const enablePush = useCallback(async () => {
    if (!supported || isBusy) {
      return;
    }

    setIsBusy(true);
    setLastError(null);

    try {
      persistAppPushEnabled(true);
      setAppPushEnabled(true);
      setPromptDismissed(false);

      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      if (permission === 'granted') {
        await syncSubscription(true);
      } else {
        setSubscriptionState('unsubscribed');
      }
    } catch (error) {
      console.error('[usePushNotifications] Failed to enable push:', error);
      setSubscriptionState('error');
      setLastError(error instanceof Error ? error.message : 'Unable to enable push notifications.');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, supported, syncSubscription]);

  const disablePush = useCallback(async () => {
    if (!supported || isBusy) {
      return;
    }

    setIsBusy(true);
    setLastError(null);

    try {
      persistAppPushEnabled(false);
      setAppPushEnabled(false);
      await postConfigToServiceWorker(gatewayToken, effectiveUserLabel, false);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      setPermissionState(Notification.permission);
      setSubscriptionState('unsubscribed');
    } catch (error) {
      console.error('[usePushNotifications] Failed to disable push:', error);
      setSubscriptionState('error');
      setLastError(error instanceof Error ? error.message : 'Unable to disable push notifications.');
    } finally {
      setIsBusy(false);
    }
  }, [effectiveUserLabel, gatewayToken, isBusy, supported]);

  const dismissPrompt = useCallback(() => {
    setPromptDismissed(true);
  }, []);

  useEffect(() => {
    if (!supported) {
      setPermissionState('unsupported');
      setSubscriptionState('unsubscribed');
      return;
    }

    setPermissionState(Notification.permission);
  }, [supported]);

  useEffect(() => {
    if (!supported || !connected) {
      return;
    }

    void postConfigToServiceWorker(gatewayToken, effectiveUserLabel, appPushEnabled).catch((error) => {
      console.debug('[usePushNotifications] Failed to post push config to service worker:', error);
    });
  }, [appPushEnabled, connected, effectiveUserLabel, gatewayToken, supported]);

  useEffect(() => {
    if (!supported || !connected) {
      setSubscriptionState(connected ? 'unsubscribed' : 'idle');
      return;
    }

    setPermissionState(Notification.permission);

    if (Notification.permission === 'granted' && appPushEnabled) {
      void syncSubscription();
      return;
    }

    if (Notification.permission !== 'granted') {
      setSubscriptionState('unsubscribed');
    }
  }, [appPushEnabled, connected, supported, syncSubscription]);

  useEffect(() => {
    if (!supported) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void refresh();
      }
    };

    const handleServiceWorkerMessage = (event: MessageEvent<ServiceWorkerPushMessage>) => {
      const data = event.data;
      if (!data || typeof data !== 'object') {
        return;
      }

      if (data.type === 'push-subscription-change') {
        setPermissionState(Notification.permission);
        if (data.ok) {
          setSubscriptionState('subscribed');
          setLastError(null);
        } else if (data.error) {
          setSubscriptionState('error');
          setLastError(data.error);
        } else {
          void refresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [refresh, supported]);

  const promptVisible = connected && supported && permissionState === 'default' && !promptDismissed;

  const statusLabel = useMemo(() => {
    if (!supported) {
      return 'Unsupported';
    }
    if (permissionState === 'denied') {
      return 'Blocked';
    }
    if (permissionState === 'default') {
      return 'Off';
    }
    if (!appPushEnabled) {
      return 'Disabled';
    }
    if (subscriptionState === 'syncing') {
      return 'Syncing…';
    }
    if (subscriptionState === 'subscribed') {
      return 'Enabled';
    }
    if (subscriptionState === 'error') {
      return 'Needs attention';
    }
    return 'Off';
  }, [appPushEnabled, permissionState, subscriptionState, supported]);

  const statusHint = useMemo(() => {
    if (!supported) {
      return 'This browser does not support web push notifications.';
    }
    if (permissionState === 'denied') {
      return 'Notifications are blocked — enable them in the browser or PWA settings.';
    }
    if (permissionState === 'default') {
      return 'Notifications are available, but you have not enabled them yet.';
    }
    if (!appPushEnabled) {
      return 'Push notifications are paused for this device.';
    }
    if (subscriptionState === 'subscribed') {
      return 'This device is subscribed and ready to receive push notifications.';
    }
    if (subscriptionState === 'syncing') {
      return 'Finishing push subscription setup…';
    }
    if (subscriptionState === 'error') {
      return lastError ?? 'Push notifications could not be fully configured.';
    }
    return 'Push notifications are currently off.';
  }, [appPushEnabled, lastError, permissionState, subscriptionState, supported]);

  return {
    supported,
    permissionState,
    subscriptionState,
    appPushEnabled,
    isBusy,
    lastError,
    promptVisible,
    statusLabel,
    statusHint,
    enablePush,
    disablePush,
    dismissPrompt,
    refresh,
  };
}
